import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { authedAxios, print } from '../../utils'
import { Add, Edit, Print } from '@mui/icons-material'
import EditPlan from './EditPlan'
import { STRATEGY_PLAN_SECTIONS } from '../../constants'
import GenericLoadError from '../../common/GenericLoadError'
import { grey } from '@mui/material/colors'
import useIdentities from '../../hooks/api/useIdentities'
import useIsAdmin from '../../hooks/useIsAdmin'
import useCurrentIdentity from '../../hooks/useCurrentIdentity'
import { getIdentityName } from '../../utils/identity'
import useAlert from '../../hooks/useAlert'

const Strategy = () => {
  const [plan, setPlan] = useState(null)
  const [isEditingPlan, setIsEditingPlan] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const { isPriveledged } = useIsAdmin()

  const { identities, ..._identities } = useIdentities(
    null,
    {},
    { skip: !isPriveledged }
  )

  const { currentIdentity } = useCurrentIdentity()

  const getIdentityById = (id) => identities.find((i) => i.id === id)

  const [selectedIdentity, setSelectedIdentity] = useState(
    getIdentityById(currentIdentity.id) || currentIdentity
  )

  const getDashboardSummary = () => {
    setPlan(null)
    setIsLoading(true)
    return authedAxios
      .get(`/api/planning/${selectedIdentity.id}`)
      .then((res) => {
        setPlan(res.data.plan)
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const { createAlert, createGenericErrorAlert } = useAlert()

  const onSave = (newPlan) => {
    return authedAxios
      .put(`/api/planning/${selectedIdentity.id}`, newPlan)
      .then((_res) => {
        setPlan(newPlan)
        setIsEditingPlan(false)
        createAlert({ message: 'Plan saved succesfully' })
      })
      .catch((e) => {
        createGenericErrorAlert()
      })
  }

  useEffect(() => {
    if (!selectedIdentity) return
    getDashboardSummary()
  }, [selectedIdentity?.id])

  if (_identities.isLoading) return <CircularProgress />
  if (hasError || _identities.hasLoadError) return <GenericLoadError />

  const isEmpty = !plan

  return (
    <>
      <Box>
        <TextField
          label={'Whose plan?'}
          select
          value={selectedIdentity?.id || ''}
          onChange={(e) => setSelectedIdentity(getIdentityById(e.target.value))}
          sx={{ mb: 2, minWidth: '300px' }}
        >
          {identities.map((i) => (
            <MenuItem value={i.id}>
              {currentIdentity.id === i.id
                ? 'My plan'
                : `${getIdentityName(i)}'s plan`}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading && <CircularProgress />}
      {isEditingPlan && (
        <EditPlan
          plan={plan}
          onSave={onSave}
          selectedIdentity={selectedIdentity}
          onCancel={() => setIsEditingPlan(false)}
        />
      )}

      {!isEditingPlan && (
        <>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {!isEmpty && (
              <Button
                variant={'outlined'}
                endIcon={<Edit />}
                onClick={() => setIsEditingPlan(true)}
              >
                Edit
              </Button>
            )}

            {/* <Button
              variant={'outlined'}
              endIcon={<Print />}
              onClick={() => {
                print('strategy-container')
              }}
            >
              Print
            </Button> */}
          </Box>
          <Box id={'strategy-container'}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '18px', mr: 2 }}>
                <b>Strategy</b>
              </Typography>

              {isEmpty && (
                <>
                  <Button
                    sx={{ ml: 6 }}
                    variant={'outlined'}
                    onClick={() => setIsEditingPlan(true)}
                    startIcon={<Add />}
                  >
                    Create a plan
                  </Button>
                </>
              )}
            </Box>

            {isEmpty && (
              <Typography>
                <i>No plan found</i>
              </Typography>
            )}

            {!isEmpty && (
              <Box sx={{ mt: 2, ml: 2 }}>
                {Object.keys(STRATEGY_PLAN_SECTIONS).map((key) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '16px', mb: 0.5 }}>
                      <b>{STRATEGY_PLAN_SECTIONS[key]}</b>
                    </Typography>
                    {plan[key] && <Typography sx={{}}>{plan[key]}</Typography>}
                    {!plan[key] && (
                      <Typography sx={{ color: grey['500'] }}>
                        <i>Empty</i>
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  )
}

const Wrapper = (props) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mt: 2 }}>
      <Strategy {...props} />
    </Box>
  )
}

export default Wrapper
